
import { useState, useEffect } from 'react';
import { ConsentData, ConsentCategories } from './consent/types';
import { loadStoredConsent, saveConsent, clearStoredConsent } from './consent/storage';
import { useGdprSettings } from './consent/gdprSettings';
import { saveConsentToDatabase } from './consent/consentOperations';

export const useConsentManager = () => {
  const [consent, setConsent] = useState<ConsentData | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  const { gdprSettings, isLoading: isGdprLoading } = useGdprSettings();

  useEffect(() => {
    const initializeConsent = async () => {
      try {
        setIsLoading(true);
        
        if (!gdprSettings) return;

        // Load existing consent
        const existingConsent = loadStoredConsent();
        
        if (existingConsent) {
          setConsent(existingConsent);
        }

        // Determine if we should show the banner
        const shouldShow = gdprSettings.gdpr_enabled && !existingConsent;
        setShowBanner(shouldShow);
      } catch (error) {
        // Silent error handling
      } finally {
        setIsLoading(false);
      }
    };

    if (!isGdprLoading) {
      initializeConsent();
    }
  }, [gdprSettings, isGdprLoading]);

  const updateConsent = async (newConsent: ConsentCategories) => {
    try {
      const consentData: ConsentData = {
        ...newConsent,
        timestamp: new Date().toISOString(),
        version: '1.0'
      };

      setConsent(consentData);
      saveConsent(consentData);
      setShowBanner(false);

      // Save to database
      await saveConsentToDatabase(consentData);
    } catch (error) {
      // Silent error handling
    }
  };

  const acceptAll = () => {
    const allCategories: ConsentCategories = {
      necessary: true,
      analytics: true,
      advertising: true,
      preferences: true
    };
    updateConsent(allCategories);
  };

  const rejectAll = () => {
    const necessaryOnly: ConsentCategories = {
      necessary: true,
      analytics: false,
      advertising: false,
      preferences: false
    };
    updateConsent(necessaryOnly);
  };

  const revokeConsent = () => {
    clearStoredConsent();
    setConsent(null);
    setShowBanner(gdprSettings?.gdpr_enabled || false);
  };

  return {
    consent,
    showBanner,
    isLoading: isLoading || isGdprLoading,
    gdprSettings,
    updateConsent,
    acceptAll,
    rejectAll,
    revokeConsent
  };
};
